import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { Parking, ParkingsState, ParkingFormData } from '../../resources/types/parkingsTypes';
import { handleDefaultErrors } from '../../resources/functions';

const initialState: ParkingsState = {
  parkings: [],
  loading: false,
  shouldUpdate: true,
};

export const parkingsSlice = createSlice({
  name: 'parkings',
  initialState,
  reducers: {
    setParkings: (state, action: PayloadAction<Parking[]>) => {
      state.parkings = action.payload;
      state.shouldUpdate = false;
      state.loading = false;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setShouldUpdate: (state, action: PayloadAction<boolean>) => {
      state.shouldUpdate = action.payload;
    },
    setParkingsHiddenInApp: (state, action: PayloadAction<{ ids: string[]; hidden: boolean }>) => {
      const { ids, hidden } = action.payload;
      state.parkings.forEach(parking => {
        if (ids.includes(parking._id)) parking.hiddenInApp = hidden;
      });
    },
  },
});

const { setParkings, setLoading, setShouldUpdate, setParkingsHiddenInApp } = parkingsSlice.actions;

export const getParkingsAsync = (forceUpdate?: boolean): AppThunk => async (dispatch, getState) => {
  if (!getState().parkings.shouldUpdate && !forceUpdate) return;
  try {
    dispatch(setLoading(true));
    const response = await axios.get('/parkings');
    dispatch(setParkings(response.data.data || []));
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const createParkingAsync = (
  data: ParkingFormData,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.post('/parkings', data);
    dispatch(setShouldUpdate(true));
    await dispatch(getParkingsAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const updateParkingAsync = (
  id: string,
  data: Partial<ParkingFormData>,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.put(`/parkings/${id}`, data);
    dispatch(setShouldUpdate(true));
    await dispatch(getParkingsAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

// Optimistic: flip the switches immediately, roll back the ones the backend rejected
export const setParkingsHiddenInAppAsync = (
  ids: string[],
  hidden: boolean
): AppThunk => async dispatch => {
  dispatch(setParkingsHiddenInApp({ ids, hidden }));
  const failedIds: string[] = [];
  let lastError: unknown = null;
  for (const id of ids) {
    try {
      await axios.put(`/parkings/${id}`, { hiddenInApp: hidden });
    } catch (error) {
      failedIds.push(id);
      lastError = error;
    }
  }
  if (failedIds.length > 0) {
    dispatch(setParkingsHiddenInApp({ ids: failedIds, hidden: !hidden }));
    handleDefaultErrors(lastError);
  }
};

export const deleteParkingAsync = (
  id: string,
  onSuccess?: () => void
): AppThunk => async dispatch => {
  try {
    dispatch(setLoading(true));
    await axios.delete(`/parkings/${id}`);
    dispatch(setShouldUpdate(true));
    await dispatch(getParkingsAsync(true));
    onSuccess?.();
  } catch (error) {
    dispatch(setLoading(false));
    handleDefaultErrors(error);
  }
};

export const uploadParkingPhotoAsync = async (file: File): Promise<string | null> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post('/files/persistent', formData);
    console.log('Upload response:', response.data);
    return response.data.path || response.data.filename || response.data;
  } catch (error) {
    console.error('Upload error:', error);
    handleDefaultErrors(error);
    return null;
  }
};

export const selectParkings = (state: RootState) => state.parkings;

export default parkingsSlice.reducer;
